import { useRequestReservationForProject } from "@/Apollo/hooks/useProBuyerProjects";
import {
  ReservationMutationResult,
  parseBuyerReservation,
} from "@/features/pro-buyer-reservations/data/reservations-client";
import {
  RESERVATIONS_QUERY_KEY,
  RESERVATION_REFETCH_DELAY_MS,
  applyReservationTransition,
} from "@/features/pro-buyer-reservations/hooks/useReservations";
import { useQueryClient } from "@tanstack/react-query";

type GraphQlErrorLike = {
  graphQLErrors?: readonly { extensions?: { code?: unknown } }[];
};

const errorCode = (error: unknown): string | null => {
  const code = (error as GraphQlErrorLike | null)?.graphQLErrors?.[0]
    ?.extensions?.code;

  return typeof code === "string" ? code : null;
};

/**
 * WHO-5959 6e, spec S8. Sends the reservation request with a project. The
 * product page and Dashboard read reservations through React Query
 * (reservations-client), so the answer goes into those caches the same way
 * `useReservationActions` does after a transition (WHO-5947 F22: the replica
 * lags, the mutation's own answer is authoritative).
 */
export const useReservationProjectRequest = () => {
  const [mutate] = useRequestReservationForProject();
  const queryClient = useQueryClient();

  return async (
    productId: string,
    projectId: string
  ): Promise<ReservationMutationResult> => {
    try {
      const result = await mutate({ variables: { productId, projectId } });
      const reservation = parseBuyerReservation(
        result.data?.requestReservation
      );

      if (reservation) {
        applyReservationTransition(queryClient, reservation, "buyer");
      }

      setTimeout(
        () =>
          void queryClient.invalidateQueries({
            queryKey: RESERVATIONS_QUERY_KEY,
          }),
        RESERVATION_REFETCH_DELAY_MS
      );

      return { ok: true, reservation };
    } catch (error) {
      await queryClient.invalidateQueries({ queryKey: RESERVATIONS_QUERY_KEY });

      return { ok: false, code: errorCode(error) };
    }
  };
};
